import { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  useRouterState,
  redirect,
  isRedirect,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { CookieBanner } from "@/components/cookie-banner";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { Toaster } from "sonner";
import { AnalyticsProvider } from "@/lib/analytics";
import { ScrollToTop } from "@/components/scroll-to-top";
import heroImage from "@/assets/hero-1.jpg";
import pluggzLogo from "@/assets/pluggz-logo.png";

function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  beforeLoad: async ({ location }) => {
    // MFA enforcement: if the user has a verified factor but the session is
    // still AAL1, force them through the dedicated challenge before any
    // protected content renders. Skip on SSR (no localStorage session) and on
    // auth-related routes to avoid loops.
    if (typeof window === "undefined") return;
    const path = location.pathname;
    const exempt =
      path === "/mfa-challenge" ||
      path === "/login" ||
      path === "/admin-login" ||
      path === "/signup" ||
      path === "/forgot-password" ||
      path === "/reset-password" ||
      path.startsWith("/auth");
    if (exempt) return;
    try {
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal && aal.currentLevel === "aal1" && aal.nextLevel === "aal2") {
        throw redirect({ to: "/mfa-challenge", search: { redirect: location.href } });
      }
    } catch (e) {
      // Re-throw redirects; swallow probe errors so the app still renders.
      if (isRedirect(e)) throw e;
    }
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Pluggz" },
      { property: "og:site_name", content: "Pluggz" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "theme-color", content: "#0d0d0d" },
      { name: "msvalidate.01", content: "43181E8C63AE97389972D139CCFD2169" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "preload", as: "image", href: heroImage, fetchpriority: "high" },
      { rel: "preload", as: "image", href: pluggzLogo, fetchpriority: "high" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Inter+Tight:wght@300;400;500;600&display=swap",
        fetchpriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Pluggz",
          "url": "https://www.pluggzofficial.com",
          "logo": "https://www.pluggzofficial.com/pluggz-logo.png",
          "description": "Pluggz is a social affiliate ecommerce platform empowering digital creators to build personalized storefronts connecting consumers with influencer curations",
          "sameAs": ["https://www.instagram.com/pluggzofficial", "https://www.tiktok.com/@pluggzofficial"],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <script
          dangerouslySetInnerHTML={{
            __html: `(function() { try { const t = localStorage.getItem('pluggz.theme'); if (t === 'dark') document.documentElement.classList.add('dark'); } catch(e) {} })();`,
          }}
        />
        <HeadContent />
      </head>
      <body className="relative">
        <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 opacity-50 mesh-bg" />
        <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 [background-image:radial-gradient(rgba(0,0,0,0.025)_1px,transparent_1px)] [background-size:4px_4px] mix-blend-multiply" />
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function PageLoadingBar() {
  const router = useRouter();
  const isPending = router.state.status === "pending";
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const update = () => {
      const scrollTop = window.scrollY;
      const height = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(height > 0 ? Math.min(100, (scrollTop / height) * 100) : 0);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const width = isPending ? 100 : progress;

  return (
    <div
      aria-hidden="true"
      className="fixed top-0 left-0 z-50 h-[4px] bg-foreground transition-[width] duration-150 ease-out"
      style={{ width: `${width}%` }}
    />
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isChromeless = pathname.startsWith("/bio/") || pathname.startsWith("/embed/") || pathname === "/demo";

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorComponent error={error as Error} reset={resetError} />
      )}
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <MaintenanceGate>
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:bg-foreground focus:px-4 focus:py-2 focus:text-background"
            >
              Skip to main content
            </a>
            <PageLoadingBar />
            {!isChromeless && <SiteHeader />}
            <main id="main-content" tabIndex={-1}>
              <Outlet />
            </main>
            {!isChromeless && <SiteFooter />}
            <CookieBanner />
            <Toaster position="bottom-right" richColors />
            <AnalyticsProvider />
            <ScrollToTop />
          </MaintenanceGate>
        </AuthProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const envFlag = import.meta.env.VITE_MAINTENANCE_MODE === "true";

  const { data: maintenanceMode } = useQuery({
    queryKey: ["maintenance-mode"],
    queryFn: async () => {
      const { data } = await supabase
        .from("platform_settings")
        .select("maintenance_mode")
        .eq("id", true)
        .maybeSingle();
      return !!data?.maintenance_mode;
    },
    staleTime: 60_000,
  });

  const { data: isAdmin } = useQuery({
    queryKey: ["is-admin-maintenance", user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      return (data ?? []).some((r) => r.role === "admin");
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  const active = envFlag || !!maintenanceMode;
  if (active && !isAdmin) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-6 text-center">
        <div className="max-w-md">
          <p className="editorial-eyebrow">Pluggz</p>
          <h1 className="mt-3 font-serif text-4xl">We'll be right back</h1>
          <p className="mt-4 text-sm text-muted-foreground">
            Pluggz is undergoing scheduled maintenance. Please check back shortly.
          </p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
